/**
 * EngineSummary Component
 *
 * Displays a summary view of engine analysis results with a compact layout.
 * Layout:
 * ┌─────────────────────────────────────────────────────────────────┐
 * │ [severity border] Score + Label + Verdict   [View Detail CTA]  │
 * │ Action sentence (1 line)                                       │
 * │ Meta line: N issues · +X pts · CVR range · install range       │
 * ├────────────────────────────────────────────────────────────────│
 * │ Key Issues (3 max)        │ Key Strengths (3 max)              │
 * │ [Show N more]             │ [Show N more]                      │
 * ├────────────────────────────────────────────────────────────────│
 * │ ▶ Full commentary                                              │
 * └─────────────────────────────────────────────────────────────────┘
 */

import { useState } from 'react';
import type { UnifiedCurrentStateSummary, UnifiedRecommendation, AsoEngineType } from '@a7y/types';
import {
  ExclamationCircleIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ChevronRightIcon,
} from '@heroicons/react/20/solid';
import { ScoreGauge } from './ScoreGauge';
import { DevCardBadge } from './DevCardBadge';
import { CollapsibleSection } from './CollapsibleSection';
import { ENGINE_DISPLAY_LABELS } from './constants';
import { parseInstallImpactRange, formatNumber } from './aggregation-utils';

export interface EngineSummaryProps {
  /** Summary data from the engine */
  summary: UnifiedCurrentStateSummary;
  /** Engine type for display label */
  engineName: AsoEngineType;
  /** Callback when "View Detailed Report" is clicked */
  onViewDetail?: () => void;
  /** Recommendations for aggregate impact display */
  recommendations?: UnifiedRecommendation[];
  /** When true, all collapsible sections are force-expanded (for PDF rendering) */
  printMode?: boolean;
  /** Additional CSS classes */
  className?: string;
}

interface AggregateImpactMetrics {
  installImpactRange: { low: number; high: number } | null;
  topCvrRange: string | null;
}

function aggregateImpactMetrics(recommendations: UnifiedRecommendation[]): AggregateImpactMetrics {
  const installImpactValues: { low: number; high: number }[] = [];
  let topCvrRange: string | null = null;
  for (const rec of recommendations) {
    const details = rec.impact?.details;
    if (!details) continue;

    // Sum install_impact from Priority 1/Priority 2 recs
    if (
      (rec.priority === 'Priority 1' || rec.priority === 'Priority 2') &&
      details.install_impact
    ) {
      const parsed = parseInstallImpactRange(details.install_impact);
      if (parsed) installImpactValues.push(parsed);
    }

    // Grab first CVR lift range
    if (!topCvrRange && details.metric_type?.toLowerCase().includes('cvr') && details.range) {
      topCvrRange = details.range;
    }
  }

  const installImpactRange =
    installImpactValues.length > 0
      ? {
          low: installImpactValues.reduce((sum, v) => sum + v.low, 0),
          high: installImpactValues.reduce((sum, v) => sum + v.high, 0),
        }
      : null;

  return { installImpactRange, topCvrRange };
}

/** Get a short verdict label based on score. */
function getScoreVerdict(score: number): string {
  if (score >= 80) return 'Strong; room for refinements.';
  if (score >= 60) return 'Good foundation; key gaps to address.';
  if (score >= 40) return 'Fair; significant improvements needed.';
  return 'Needs attention; major gaps identified.';
}

/** Get severity border color class based on score. */
function getSeverityBorderClass(score: number): string {
  if (score >= 80) return 'border-l-4 border-l-green-500';
  if (score >= 60) return 'border-l-4 border-l-blue-400';
  if (score >= 40) return 'border-l-4 border-l-amber-500';
  return 'border-l-4 border-l-red-500';
}

/** Get optional severity badge based on score. */
function getSeverityBadge(
  score: number
): { label: string; color: 'green' | 'red' | 'amber' } | null {
  if (score >= 80) return { label: 'Strong', color: 'green' };
  if (score >= 60) return null;
  if (score >= 40) return { label: 'Room for Growth', color: 'amber' };
  return { label: 'Needs Attention', color: 'red' };
}

function UnscoredPreviewGauge() {
  return (
    <div className="inline-flex h-7 w-12 items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-sm font-bold text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/40 dark:text-blue-300">
      N/A
    </div>
  );
}

/** Max bullets shown by default in issues/strengths columns. */
const MAX_VISIBLE_BULLETS = 3;

function BulletList({
  items,
  icon,
  iconClass: _iconClass,
  bulletColor,
  label,
  emptyText,
  printMode,
}: {
  items: string[];
  icon: React.ReactNode;
  iconClass?: string;
  bulletColor: string;
  label: string;
  emptyText: string;
  printMode: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const visibleItems = printMode || showAll ? items : items.slice(0, MAX_VISIBLE_BULLETS);
  const hiddenCount = items.length - MAX_VISIBLE_BULLETS;

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
        {icon}
        {label}
      </h4>
      {items.length > 0 ? (
        <>
          <ul className="space-y-1.5">
            {visibleItems.map((item, index) => (
              <li
                key={index}
                className="text-sm text-zinc-700 dark:text-zinc-200 flex items-start gap-2"
              >
                <span className={`${bulletColor} mt-1`}>&bull;</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && !printMode && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
            >
              Show {hiddenCount} more
            </button>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">{emptyText}</p>
      )}
    </div>
  );
}

export function EngineSummary({
  summary,
  engineName,
  onViewDetail,
  recommendations,
  printMode = false,
  className = '',
}: EngineSummaryProps) {
  const [commentaryExpanded, setCommentaryExpanded] = useState(false);

  const {
    overall_score,
    key_issues,
    key_strengths,
    overall_commentary,
    recommendations_summary,
    available_lift_nominal,
    available_lift_percentage,
  } = summary;

  const actionSummary = summary.action_summary;
  const displayLabel = ENGINE_DISPLAY_LABELS[engineName] ?? engineName;
  const isPredictionsOnly = engineName === 'searchTerm';
  const effectiveScore = overall_score ?? 0;
  const severityBorderClass = isPredictionsOnly
    ? 'border-l-4 border-l-blue-400'
    : getSeverityBorderClass(effectiveScore);
  const severityBadge = isPredictionsOnly
    ? { label: 'Predictions only', color: 'blue' as const }
    : getSeverityBadge(effectiveScore);
  const verdict = isPredictionsOnly
    ? 'Predicted terms are informational; no readiness score is applied.'
    : getScoreVerdict(effectiveScore);

  const impactMetrics =
    recommendations && recommendations.length > 0 ? aggregateImpactMetrics(recommendations) : null;

  // Build compact meta line parts
  const metaParts: string[] = [];
  if (key_issues.length > 0)
    metaParts.push(`${key_issues.length} issue${key_issues.length > 1 ? 's' : ''}`);
  if (available_lift_nominal > 0) metaParts.push(`+${available_lift_nominal} pts potential`);
  if (impactMetrics?.topCvrRange) metaParts.push(`CVR: ${impactMetrics.topCvrRange}`);
  if (impactMetrics?.installImpactRange) {
    metaParts.push(
      `+${formatNumber(impactMetrics.installImpactRange.low)}-${formatNumber(impactMetrics.installImpactRange.high)} installs/mo`
    );
  }

  return (
    <DevCardBadge name="EngineSummary">
      <div
        className={`border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden ${severityBorderClass} ${className}`}
      >
        {/* Header: Score + Label + Verdict + CTA */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-zinc-800 border-b border-gray-200 dark:border-zinc-700 flex items-center gap-4">
          {isPredictionsOnly ? (
            <UnscoredPreviewGauge />
          ) : (
            <ScoreGauge score={effectiveScore} size="small" />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {displayLabel}
              </h3>
              {severityBadge && (
                <span
                  className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                    severityBadge.color === 'green'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : severityBadge.color === 'blue'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        : severityBadge.color === 'amber'
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  }`}
                >
                  {severityBadge.label}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{verdict}</p>
          </div>
          {onViewDetail && (
            <button
              onClick={onViewDetail}
              className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
            >
              View Detail
              <ChevronRightIcon className="size-4" />
            </button>
          )}
        </div>

        <div className="p-4 space-y-3">
          {/* Action sentence */}
          {(actionSummary || overall_commentary) && (
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {actionSummary || overall_commentary?.split(/[.!]\s/)[0] + '.'}
            </p>
          )}

          {/* Meta line */}
          {metaParts.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              {metaParts.map((part, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  {i > 0 && <span className="text-gray-300 dark:text-gray-600">&middot;</span>}
                  {part}
                </span>
              ))}
            </div>
          )}

          {/* Issues + Strengths (capped at 3) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-gray-200 dark:border-zinc-700">
            <BulletList
              items={key_issues}
              icon={<ExclamationCircleIcon className="size-4 text-red-500" />}
              bulletColor="text-red-400"
              label="Top Issues"
              emptyText={
                isPredictionsOnly
                  ? 'No scored issues; predictions only'
                  : 'No major issues identified'
              }
              printMode={printMode}
            />
            <BulletList
              items={key_strengths ?? []}
              icon={<CheckCircleIcon className="size-4 text-green-500" />}
              bulletColor="text-green-400"
              label="Key Strengths"
              emptyText="No key strengths highlighted"
              printMode={printMode}
            />
          </div>

          {/* Full commentary (collapsed by default) */}
          {(overall_commentary || recommendations_summary) && (
            <CollapsibleSection
              title="Full commentary"
              headerVariant="minimal"
              isExpanded={commentaryExpanded}
              onToggle={() => setCommentaryExpanded(!commentaryExpanded)}
              forceExpanded={printMode}
            >
              <div className="space-y-2">
                {overall_commentary && (
                  <p className="text-sm text-zinc-700 dark:text-zinc-200 leading-relaxed">
                    {overall_commentary}
                  </p>
                )}
                {recommendations_summary && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                    {recommendations_summary}
                  </p>
                )}
                {(available_lift_nominal > 0 || impactMetrics?.topCvrRange) && (
                  <div className="flex flex-wrap items-center gap-2">
                    {available_lift_nominal > 0 && (
                      <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded-full">
                        <ArrowTrendingUpIcon className="size-4 text-blue-500" />
                        <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                          +{available_lift_nominal} pts
                        </span>
                        <span className="text-xs text-blue-500/70">
                          ({available_lift_percentage}%)
                        </span>
                      </div>
                    )}
                    {impactMetrics?.topCvrRange && (
                      <div className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 dark:bg-purple-900/30 rounded-full">
                        <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                          CVR: {impactMetrics.topCvrRange}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CollapsibleSection>
          )}
        </div>
      </div>
    </DevCardBadge>
  );
}
